import { Link, useRouter } from '@tanstack/react-router';
import {
  Bot,
  Building2,
  DollarSign,
  FileSpreadsheet,
  LayoutDashboard,
  Menu,
  PieChart,
  Receipt,
  RefreshCw,
  Settings,
  Tags,
  Target,
  TrendingUp,
  Wallet,
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

const navigation = [
  {
    name: 'Visão Geral',
    href: '/dashboard',
    icon: LayoutDashboard,
  },
  {
    name: 'Transações',
    href: '/transactions',
    icon: Receipt,
  },
  {
    name: 'Categorias',
    href: '/categories',
    icon: PieChart,
  },
  {
    name: 'Gastos Fixos',
    href: '/fixed-expenses',
    icon: RefreshCw,
  },
  {
    name: 'Fontes de Renda',
    href: '/income-sources',
    icon: DollarSign,
  },
  {
    name: 'Fluxo de Caixa',
    href: '/cashflow',
    icon: Wallet,
  },
  {
    name: 'Orçamentos',
    href: '/budgets',
    icon: Target,
  },
  {
    name: 'Tendências',
    href: '/trends',
    icon: TrendingUp,
  },
  {
    name: 'Tags',
    href: '/tags-dashboard',
    icon: Tags,
  },
  {
    name: 'Importar CSV',
    href: '/csv-import',
    icon: FileSpreadsheet,
  },
  {
    name: 'IA Chat',
    href: '/ai-chat',
    icon: Bot,
  },
];

interface SidebarProps {
  className?: string;
}

export function Sidebar({ className }: SidebarProps) {
  const router = useRouter();

  return (
    <div className={cn('flex h-full flex-col', className)}>
      {/* Household Selector */}
      <div className="flex justify-center border-b p-3">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button className="h-10 w-10 rounded-lg p-0" variant="ghost">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600">
                <Building2 className="h-4 w-4 text-white" />
              </div>
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={12}>
            <div>
              <div className="font-medium">Felix Family</div>
              <div className="text-xs opacity-80">4 membros</div>
            </div>
          </TooltipContent>
        </Tooltip>
      </div>

      {/* Navigation Icons */}
      <div className="flex-1 p-3">
        <div className="flex flex-col items-center space-y-2">
          {navigation.map((item) => {
            const isActive = router.state.location.pathname === item.href;
            return (
              <Tooltip key={item.name}>
                <TooltipTrigger asChild>
                  <Link
                    className={cn(
                      'flex h-10 w-10 items-center justify-center rounded-lg transition-colors',
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                    )}
                    to={item.href}
                  >
                    <item.icon className="h-5 w-5" />
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="right" sideOffset={12}>
                  {item.name}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </div>

      {/* Bottom Section */}
      <div className="flex justify-center border-t p-3">
        <Tooltip>
          <TooltipTrigger asChild>
            <Link
              className={cn(
                'flex h-10 w-10 items-center justify-center rounded-lg transition-colors',
                router.state.location.pathname === '/settings'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              )}
              to="/settings"
            >
              <Settings className="h-5 w-5" />
            </Link>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={12}>
            Configurações
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}

function MobileSidebarContent() {
  const router = useRouter();

  return (
    <div className="flex h-full flex-col">
      {/* Household Selector */}
      <div className="flex items-center gap-3 border-b p-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600">
          <Building2 className="h-5 w-5 text-white" />
        </div>
        <div>
          <div className="font-medium">Felix Family</div>
          <div className="text-muted-foreground text-xs">4 membros</div>
        </div>
      </div>

      {/* Navigation with Text */}
      <div className="flex-1 p-4">
        <div className="space-y-2">
          {navigation.map((item) => {
            const isActive = router.state.location.pathname === item.href;
            return (
              <Link
                className={cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                )}
                key={item.name}
                to={item.href}
              >
                <item.icon className="h-5 w-5" />
                <span>{item.name}</span>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Settings at Bottom */}
      <div className="border-t p-4">
        <Link
          className={cn(
            'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
            router.state.location.pathname === '/settings'
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
          )}
          to="/settings"
        >
          <Settings className="h-5 w-5" />
          <span>Configurações</span>
        </Link>
      </div>
    </div>
  );
}

export function MobileSidebar() {
  const [open, setOpen] = useState(false);

  return (
    <Sheet onOpenChange={setOpen} open={open}>
      <SheetTrigger asChild>
        <Button
          className="mr-2 px-0 text-base hover:bg-transparent focus-visible:bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 md:hidden"
          variant="ghost"
        >
          <Menu className="h-6 w-6" />
          <span className="sr-only">Toggle Menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent className="pr-0" side="left">
        <MobileSidebarContent />
      </SheetContent>
    </Sheet>
  );
}
